import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import AddDialog from "../../components/Modal/AddModal";
import TextInput from "../../components/Select/InputText";
import SelectInput from "../../components/Select/SelectInput";
import TableLoadingRow from "../../components/TableLoadingRow";
import { useAuth } from "../../context/AuthContext";
import { createRole, deleteRole, getPermissions, getRoleById, getRoles, updateRole } from "../../services/rolesService";
import { addGymUser, deleteGymUser, getGymUsers, updateGymUser } from "../../services/usersService";
import { showErrorToast, showSuccessToast } from "../../services/toastService";
import { hasPermission } from "../../utils/permissions";

type TabKey = "users" | "roles";

type RoleOption = {
    _id: string;
    name: string;
    description: string;
    permissions: string[];
    isSystemRole: boolean;
    isActive: boolean;
};

type GymUser = {
    _id: string;
    gymName: string;
    email: string;
    mobile: string;
    address: string;
    roleId: string;
    roleName: string;
    isActive: boolean;
};

type PermissionItem = {
    key: string;
    label: string;
    action: string;
};

type PermissionModule = {
    key: string;
    label: string;
    permissions: PermissionItem[];
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

const readStringArray = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === "string");
};

const readErrorMessage = (err: unknown, fallback: string): string => {
    if (!err || typeof err !== "object") return fallback;
    const errObj = err as { response?: { data?: { message?: string; error?: string } } };
    return errObj.response?.data?.message ?? errObj.response?.data?.error ?? fallback;
};

const normalizeStatus = (value: unknown): boolean => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value.toLowerCase() === "active" || value.toLowerCase() === "true";
    if (typeof value === "undefined") return true;
    return Boolean(value);
};

const readRoleId = (value: unknown): string => {
    if (typeof value === "string") return value;
    const role = asRecord(value);
    return asString(role._id) || asString(role.id);
};

const readRoleName = (value: unknown): string => {
    if (typeof value === "string") return value;
    const role = asRecord(value);
    return asString(role.name) || asString(role.label) || asString(role.role) || "-";
};

const normalizeUser = (entry: unknown): GymUser => {
    const user = asRecord(entry);
    return {
        _id: asString(user._id) || asString(user.id) || asString(user.email) || crypto.randomUUID(),
        gymName: asString(user.gymName) || asString(user.fullName) || asString(user.name),
        email: asString(user.email),
        mobile: asString(user.mobile),
        address: asString(user.address),
        roleId: readRoleId(user.role),
        roleName: readRoleName(user.role),
        isActive: normalizeStatus(user.isActive ?? user.status),
    };
};

const normalizeRole = (entry: unknown): RoleOption => {
    const role = asRecord(entry);
    return {
        _id: asString(role._id) || asString(role.id) || asString(role.name) || crypto.randomUUID(),
        name: asString(role.name),
        description: asString(role.description),
        permissions: readStringArray(role.permissions),
        isSystemRole: Boolean(role.isSystemRole ?? role.systemRole),
        isActive: normalizeStatus(role.isActive ?? role.status),
    };
};

const normalizePermissionModules = (entries: unknown[]): PermissionModule[] => {
    return entries
        .map((entry) => {
            const module = asRecord(entry);
            const moduleKey = asString(module.key) || asString(module.module);
            const modulePermissions = Array.isArray(module.permissions) ? module.permissions : [];

            return {
                key: moduleKey,
                label: asString(module.label) || moduleKey,
                permissions: modulePermissions
                    .map((permissionEntry) => {
                        const permission = asRecord(permissionEntry);
                        const key = asString(permission.key);
                        return {
                            key,
                            label: asString(permission.label) || key,
                            action: asString(permission.action),
                        };
                    })
                    .filter((permission) => permission.key),
            };
        })
        .filter((module) => module.key && module.permissions.length);
};

const StatusBadge = ({ active }: { active: boolean }) => (
    <span className={`rounded-md px-2 py-1 text-xs font-medium ${active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
        {active ? "Active" : "Inactive"}
    </span>
);

const UserManagementPage = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<TabKey>("users");
    const [users, setUsers] = useState<GymUser[]>([]);
    const [roles, setRoles] = useState<RoleOption[]>([]);
    const [permissionModules, setPermissionModules] = useState<PermissionModule[]>([]);
    const [usersSearch, setUsersSearch] = useState("");
    const [rolesSearch, setRolesSearch] = useState("");
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [loadingPermissions, setLoadingPermissions] = useState(false);
    const [error, setError] = useState("");
    const [userDialogOpen, setUserDialogOpen] = useState(false);
    const [roleDialogOpen, setRoleDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<GymUser | null>(null);
    const [editingRole, setEditingRole] = useState<RoleOption | null>(null);
    const canWriteUsers = hasPermission(user, "users.write");
    const canWriteRoles = hasPermission(user, "users.write") || hasPermission(user, "settings.write");

    const fetchUsers = useCallback(async () => {
        setLoadingUsers(true);
        setError("");

        try {
            const response = await getGymUsers();
            setUsers(response.map(normalizeUser));
        } catch (err: unknown) {
            console.error("Failed to fetch users", err);
            setError(readErrorMessage(err, "Failed to load users"));
        } finally {
            setLoadingUsers(false);
        }
    }, []);

    const fetchRoles = useCallback(async () => {
        setLoadingRoles(true);
        setError("");

        try {
            const response = await getRoles();
            setRoles(response.map(normalizeRole));
        } catch (err: unknown) {
            console.error("Failed to fetch roles", err);
            setError(readErrorMessage(err, "Failed to load roles"));
        } finally {
            setLoadingRoles(false);
        }
    }, []);

    const fetchPermissions = useCallback(async () => {
        setLoadingPermissions(true);

        try {
            const response = await getPermissions();
            setPermissionModules(normalizePermissionModules(response));
        } catch (err: unknown) {
            console.error("Failed to fetch permissions", err);
            showErrorToast(readErrorMessage(err, "Failed to load permissions"));
        } finally {
            setLoadingPermissions(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
        fetchRoles();
        fetchPermissions();
    }, [fetchUsers, fetchRoles, fetchPermissions]);

    const roleOptions = useMemo(
        () =>
            roles
                .filter((role) => role.isActive || role._id === editingUser?.roleId)
                .map((role) => ({ value: role._id, label: role.name })),
        [roles, editingUser?.roleId]
    );

    const allPermissionKeys = useMemo(
        () => permissionModules.flatMap((module) => module.permissions.map((permission) => permission.key)),
        [permissionModules]
    );

    const viewPermissions = useMemo(
        () => permissionModules
            .flatMap((module) => module.permissions)
            .filter((permission) => permission.action === "view")
            .map((permission) => permission.key),
        [permissionModules]
    );

    const filteredUsers = useMemo(() => {
        const query = usersSearch.trim().toLowerCase();
        if (!query) return users;
        return users.filter((entry) =>
            [entry.gymName, entry.email, entry.mobile, entry.roleName, entry.isActive ? "active" : "inactive"]
                .some((value) => value.toLowerCase().includes(query))
        );
    }, [users, usersSearch]);

    const filteredRoles = useMemo(() => {
        const query = rolesSearch.trim().toLowerCase();
        if (!query) return roles;
        return roles.filter((entry) =>
            [entry.name, entry.description, String(entry.permissions.length), entry.isSystemRole ? "system" : "custom", entry.isActive ? "active" : "inactive"]
                .some((value) => value.toLowerCase().includes(query))
        );
    }, [roles, rolesSearch]);

    const userFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            gymName: editingUser?.gymName ?? "",
            email: editingUser?.email ?? "",
            mobile: editingUser?.mobile ?? "",
            address: editingUser?.address ?? "",
            role: editingUser?.roleId ?? "",
            password: "",
            isActive: editingUser?.isActive === false ? "inactive" : "active",
        },
        validationSchema: Yup.object({
            gymName: Yup.string().trim(),
            email: Yup.string().trim().email("Invalid email").required("Email is required"),
            mobile: Yup.string().trim(),
            address: Yup.string().trim(),
            role: Yup.string().required("Role is required"),
            password: editingUser
                ? Yup.string().min(6, "Password must be at least 6 characters")
                : Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
            isActive: Yup.string().oneOf(["active", "inactive"]).required("Status is required"),
        }),
        onSubmit: async (values, { resetForm }) => {
            if (!canWriteUsers) {
                showErrorToast("You do not have permission to manage users");
                return;
            }

            try {
                if (editingUser) {
                    await updateGymUser(editingUser._id, {
                        gymName: values.gymName,
                        email: values.email,
                        mobile: values.mobile,
                        address: values.address,
                        role: values.role,
                        ...(values.password ? { password: values.password } : {}),
                        isActive: values.isActive === "active",
                    });
                    showSuccessToast("User updated");
                } else {
                    await addGymUser({
                        email: values.email,
                        password: values.password,
                        role: values.role,
                        isActive: values.isActive === "active",
                    });
                    showSuccessToast("User created");
                }

                resetForm();
                setEditingUser(null);
                setUserDialogOpen(false);
                await fetchUsers();
            } catch (err: unknown) {
                console.error("Failed to save user", err);
                showErrorToast(readErrorMessage(err, "Failed to save user"));
            }
        },
    });

    const roleFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: editingRole?.name ?? "",
            description: editingRole?.description ?? "",
            permissions: editingRole?.permissions ?? [],
            isActive: editingRole?.isActive === false ? "inactive" : "active",
        },
        validationSchema: Yup.object({
            name: Yup.string().trim().required("Role name is required"),
            description: Yup.string().trim(),
            permissions: Yup.array().of(Yup.string()),
            isActive: Yup.string().oneOf(["active", "inactive"]).required("Status is required"),
        }),
        onSubmit: async (values, { resetForm }) => {
            if (!canWriteRoles) {
                showErrorToast("You do not have permission to manage roles");
                return;
            }

            const payload = {
                name: values.name,
                description: values.description,
                permissions: values.permissions,
                isActive: values.isActive === "active",
            };

            try {
                if (editingRole) {
                    await updateRole(editingRole._id, payload);
                    showSuccessToast("Role updated");
                } else {
                    await createRole(payload);
                    showSuccessToast("Role created");
                }

                resetForm();
                setEditingRole(null);
                setRoleDialogOpen(false);
                await fetchRoles();
            } catch (err: unknown) {
                console.error("Failed to save role", err);
                showErrorToast(readErrorMessage(err, "Failed to save role"));
            }
        },
    });

    const openCreateUser = () => {
        setEditingUser(null);
        userFormik.resetForm();
        setUserDialogOpen(true);
    };

    const openEditUser = (entry: GymUser) => {
        setEditingUser(entry);
        setUserDialogOpen(true);
    };

    const closeUserDialog = () => {
        setUserDialogOpen(false);
        setEditingUser(null);
        userFormik.resetForm();
    };

    const openCreateRole = () => {
        setEditingRole(null);
        roleFormik.resetForm();
        setRoleDialogOpen(true);
    };

    const openEditRole = async (entry: RoleOption) => {
        try {
            const response = await getRoleById(entry._id);
            const role = normalizeRole(asRecord(response).data ?? response);
            setEditingRole({ ...entry, ...role, _id: entry._id });
        } catch (err: unknown) {
            console.error("Failed to fetch role details", err);
            setEditingRole(entry);
            showErrorToast(readErrorMessage(err, "Failed to load role details"));
        }

        setRoleDialogOpen(true);
    };

    const closeRoleDialog = () => {
        setRoleDialogOpen(false);
        setEditingRole(null);
        roleFormik.resetForm();
    };

    const handleDeleteUser = async (entry: GymUser) => {
        if (!canWriteUsers) return;
        if (!window.confirm(`Delete user "${entry.email}"?`)) return;

        try {
            await deleteGymUser(entry._id);
            showSuccessToast("User deleted");
            await fetchUsers();
        } catch (err: unknown) {
            console.error("Failed to delete user", err);
            showErrorToast(readErrorMessage(err, "Failed to delete user"));
        }
    };

    const handleDeleteRole = async (entry: RoleOption) => {
        if (!canWriteRoles) return;
        if (!window.confirm(`Delete role "${entry.name}"?`)) return;

        try {
            await deleteRole(entry._id);
            showSuccessToast("Role deleted");
            await fetchRoles();
        } catch (err: unknown) {
            console.error("Failed to delete role", err);
            showErrorToast(readErrorMessage(err, "Failed to delete role"));
        }
    };

    const setRolePermissions = (nextPermissions: string[]) => {
        roleFormik.setFieldValue("permissions", Array.from(new Set(nextPermissions)));
    };

    const togglePermission = (permissionKey: string) => {
        const current = roleFormik.values.permissions;
        const next = current.includes(permissionKey)
            ? current.filter((item) => item !== permissionKey)
            : [...current, permissionKey];
        setRolePermissions(next);
    };

    return (
        <div className="w-full p-6">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                    <p className="text-sm text-gray-500">Manage gym users, roles, and module permissions.</p>
                </div>
                <button
                    type="button"
                    onClick={() => {
                        fetchUsers();
                        fetchRoles();
                        fetchPermissions();
                    }}
                    disabled={loadingUsers || loadingRoles}
                    className="w-fit rounded-md bg-black px-4 py-2 text-sm text-white disabled:opacity-60"
                >
                    {loadingUsers || loadingRoles ? "Refreshing..." : "Refresh"}
                </button>
            </div>

            {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

            <div className="mb-5 border-b border-gray-200">
                <div className="flex gap-2">
                    {(["users", "roles"] as TabKey[]).map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => setActiveTab(tab)}
                            className={`border-b-2 px-4 py-3 text-sm font-semibold capitalize ${activeTab === tab
                                ? "border-[#ac120c] text-[#ac120c]"
                                : "border-transparent text-gray-500 hover:text-gray-900"
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {activeTab === "users" ? (
                <section className="space-y-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <input
                            type="text"
                            value={usersSearch}
                            onChange={(event) => setUsersSearch(event.target.value)}
                            placeholder="Search users..."
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:max-w-sm"
                        />
                        {canWriteUsers && (
                            <button type="button" onClick={openCreateUser} className="rounded-md bg-[#ac120c] px-4 py-2 text-sm font-semibold text-white">
                                Add User
                            </button>
                        )}
                    </div>

                    <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                        <table className="w-full text-sm">
                            <thead className="bg-black text-white">
                                <tr>
                                    <th className="p-3 text-left">Gym Name</th>
                                    <th className="p-3 text-left">Email</th>
                                    <th className="p-3 text-left">Mobile</th>
                                    <th className="p-3 text-left">Role</th>
                                    <th className="p-3 text-left">Status</th>
                                    {canWriteUsers && <th className="p-3 text-left">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {loadingUsers ? (
                                    <TableLoadingRow colSpan={canWriteUsers ? 6 : 5} label="Loading users..." />
                                ) : filteredUsers.length ? (
                                    filteredUsers.map((entry) => (
                                        <tr key={entry._id} className="border-t border-gray-200 hover:bg-gray-50">
                                            <td className="p-3">{entry.gymName || "-"}</td>
                                            <td className="p-3">{entry.email || "-"}</td>
                                            <td className="p-3">{entry.mobile || "-"}</td>
                                            <td className="p-3">{entry.roleName || "-"}</td>
                                            <td className="p-3"><StatusBadge active={entry.isActive} /></td>
                                            {canWriteUsers && (
                                                <td className="p-3">
                                                    <div className="flex gap-2">
                                                        <button type="button" onClick={() => openEditUser(entry)} className="rounded-md bg-gray-800 px-3 py-1 text-xs text-white">
                                                            Edit
                                                        </button>
                                                        <button type="button" onClick={() => handleDeleteUser(entry)} className="rounded-md bg-[#ac120c] px-3 py-1 text-xs text-white">
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td className="p-4 text-center text-gray-500" colSpan={canWriteUsers ? 6 : 5}>No users found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            ) : (
                <section className="space-y-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <input
                            type="text"
                            value={rolesSearch}
                            onChange={(event) => setRolesSearch(event.target.value)}
                            placeholder="Search roles..."
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:max-w-sm"
                        />
                        {canWriteRoles && (
                            <button type="button" onClick={openCreateRole} className="rounded-md bg-[#ac120c] px-4 py-2 text-sm font-semibold text-white">
                                Add Role
                            </button>
                        )}
                    </div>

                    <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                        <table className="w-full text-sm">
                            <thead className="bg-black text-white">
                                <tr>
                                    <th className="p-3 text-left">Name</th>
                                    <th className="p-3 text-left">Description</th>
                                    <th className="p-3 text-left">Permissions</th>
                                    <th className="p-3 text-left">System Role</th>
                                    <th className="p-3 text-left">Status</th>
                                    {canWriteRoles && <th className="p-3 text-left">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {loadingRoles ? (
                                    <TableLoadingRow colSpan={canWriteRoles ? 6 : 5} label="Loading roles..." />
                                ) : filteredRoles.length ? (
                                    filteredRoles.map((entry) => (
                                        <tr key={entry._id} className="border-t border-gray-200 hover:bg-gray-50">
                                            <td className="p-3 font-medium">{entry.name || "-"}</td>
                                            <td className="p-3">{entry.description || "-"}</td>
                                            <td className="p-3">{entry.permissions.length}</td>
                                            <td className="p-3">{entry.isSystemRole ? "Yes" : "No"}</td>
                                            <td className="p-3"><StatusBadge active={entry.isActive} /></td>
                                            {canWriteRoles && (
                                                <td className="p-3">
                                                    <div className="flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => openEditRole(entry)}
                                                            className="rounded-md bg-gray-800 px-3 py-1 text-xs text-white"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteRole(entry)}
                                                            className="rounded-md bg-[#ac120c] px-3 py-1 text-xs text-white"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td className="p-4 text-center text-gray-500" colSpan={canWriteRoles ? 6 : 5}>No roles found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            <AddDialog title={editingUser ? "Edit User" : "Add User"} isOpen={userDialogOpen} onClose={closeUserDialog} maxWidth="max-w-3xl">
                <form onSubmit={userFormik.handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {editingUser && (
                            <div>
                                <TextInput label="Gym Name" name="gymName" value={userFormik.values.gymName} onChange={userFormik.handleChange} />
                                {userFormik.touched.gymName && userFormik.errors.gymName && <p className="mt-1 text-sm text-red-500">{userFormik.errors.gymName}</p>}
                            </div>
                        )}

                        <div>
                            <TextInput label="Email" name="email" type="email" value={userFormik.values.email} onChange={userFormik.handleChange} />
                            {userFormik.touched.email && userFormik.errors.email && <p className="mt-1 text-sm text-red-500">{userFormik.errors.email}</p>}
                        </div>

                        {editingUser && (
                            <>
                                <TextInput label="Mobile" name="mobile" value={userFormik.values.mobile} onChange={userFormik.handleChange} />
                                <TextInput label="Address" name="address" value={userFormik.values.address} onChange={userFormik.handleChange} />
                            </>
                        )}

                        <div>
                            <SelectInput label="Role" name="role" value={userFormik.values.role} onChange={userFormik.handleChange} options={roleOptions} />
                            {userFormik.touched.role && userFormik.errors.role && <p className="mt-1 text-sm text-red-500">{userFormik.errors.role}</p>}
                        </div>

                        <SelectInput
                            label="Status"
                            name="isActive"
                            value={userFormik.values.isActive}
                            onChange={userFormik.handleChange}
                            options={[
                                { value: "active", label: "Active" },
                                { value: "inactive", label: "Inactive" },
                            ]}
                        />

                        <div>
                            <TextInput
                                label={editingUser ? "Password (optional)" : "Password"}
                                name="password"
                                type="password"
                                value={userFormik.values.password}
                                onChange={userFormik.handleChange}
                            />
                            {userFormik.touched.password && userFormik.errors.password && <p className="mt-1 text-sm text-red-500">{userFormik.errors.password}</p>}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" className="rounded border border-gray-300 px-4 py-2" onClick={closeUserDialog}>Cancel</button>
                        <button type="submit" className="rounded bg-[#ac120c] px-4 py-2 text-white">{editingUser ? "Update User" : "Add User"}</button>
                    </div>
                </form>
            </AddDialog>

            <AddDialog title={editingRole ? "Edit Role" : "Add Role"} isOpen={roleDialogOpen} onClose={closeRoleDialog} maxWidth="max-w-4xl">
                <form onSubmit={roleFormik.handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <TextInput label="Name" name="name" value={roleFormik.values.name} onChange={roleFormik.handleChange} />
                            {roleFormik.touched.name && roleFormik.errors.name && <p className="mt-1 text-sm text-red-500">{roleFormik.errors.name}</p>}
                        </div>

                        <SelectInput
                            label="Status"
                            name="isActive"
                            value={roleFormik.values.isActive}
                            onChange={roleFormik.handleChange}
                            options={[
                                { value: "active", label: "Active" },
                                { value: "inactive", label: "Inactive" },
                            ]}
                        />
                    </div>

                    <TextInput label="Description" name="description" value={roleFormik.values.description} onChange={roleFormik.handleChange} />

                    <div>
                        <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <h3 className="text-sm font-semibold text-gray-700">Permissions</h3>
                            <div className="flex flex-wrap gap-2">
                                <button type="button" onClick={() => setRolePermissions(allPermissionKeys)} className="rounded-md border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100">Select All</button>
                                <button type="button" onClick={() => setRolePermissions(viewPermissions)} className="rounded-md border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100">View All</button>
                                <button type="button" onClick={() => setRolePermissions([])} className="rounded-md border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100">Clear</button>
                            </div>
                        </div>

                        <div className="overflow-x-auto rounded-md border border-gray-200">
                            <table className="w-full text-sm">
                                <thead className="bg-black text-white">
                                    <tr>
                                        <th className="p-3 text-left">Module</th>
                                        <th className="p-3 text-left">Permissions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingPermissions ? (
                                        <TableLoadingRow colSpan={2} label="Loading permissions..." />
                                    ) : permissionModules.length ? (
                                        permissionModules.map((module) => (
                                            <tr key={module.key} className="border-t border-gray-200">
                                                <td className="p-3 font-medium">{module.label}</td>
                                                <td className="p-3">
                                                    <div className="flex flex-wrap gap-4">
                                                        {module.permissions.map((permission) => (
                                                            <label key={permission.key} className="flex items-center gap-2 text-sm">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={roleFormik.values.permissions.includes(permission.key)}
                                                                    onChange={() => togglePermission(permission.key)}
                                                                />
                                                                <span>{permission.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td className="p-4 text-center text-gray-500" colSpan={2}>No permissions available.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" className="rounded border border-gray-300 px-4 py-2" onClick={closeRoleDialog}>Cancel</button>
                        <button type="submit" className="rounded bg-[#ac120c] px-4 py-2 text-white">{editingRole ? "Update Role" : "Add Role"}</button>
                    </div>
                </form>
            </AddDialog>
        </div>
    );
};

export default UserManagementPage;
